import { z } from "zod";

// Time validation regex (HH:MM format)
const timeRegex = /^([01]\d|2[0-3]):([0-5]\d)$/;

// =====================================================
// SHIFT SCHEMAS
// =====================================================

export const shiftSchema = z.object({
  name: z.string()
    .min(1, "El nombre es requerido")
    .max(100, "Máximo 100 caracteres"),
  code: z.string()
    .min(1, "El código es requerido")
    .max(20, "Máximo 20 caracteres")
    .regex(/^[A-Z0-9-]+$/, "Solo mayúsculas, números y guiones"),
  description: z.string().max(500).optional().nullable(),
  start_time: z.string()
    .regex(timeRegex, "Formato de hora inválido (HH:MM)"),
  end_time: z.string()
    .regex(timeRegex, "Formato de hora inválido (HH:MM)"),
  crosses_midnight: z.boolean().default(false),
  color: z.string()
    .regex(/^#[0-9A-Fa-f]{6}$/, "Formato de color inválido")
    .default("#3b82f6"),
  is_active: z.boolean().default(true),
});

export type ShiftFormData = z.infer<typeof shiftSchema>;

// =====================================================
// BREAK TYPE SCHEMAS
// =====================================================

export const breakTypeSchema = z.object({
  name: z.string()
    .min(1, "El nombre es requerido")
    .max(100, "Máximo 100 caracteres"),
  code: z.string()
    .min(1, "El código es requerido")
    .max(20, "Máximo 20 caracteres")
    .regex(/^[A-Z0-9_]+$/, "Solo mayúsculas, números y guiones bajos"),
  default_duration_minutes: z.coerce.number()
    .int()
    .min(1, "Mínimo 1 minuto")
    .max(120, "Máximo 120 minutos"),
  is_paid: z.boolean().default(false),
  is_active: z.boolean().default(true),
});

export type BreakTypeFormData = z.infer<typeof breakTypeSchema>;

// =====================================================
// SHIFT BREAK SCHEMAS
// =====================================================

export const shiftBreakSchema = z.object({
  shift_id: z.string().uuid("Seleccione un turno válido"),
  break_type_id: z.string().uuid("Seleccione un tipo de descanso"),
  start_offset_minutes: z.coerce.number()
    .int()
    .min(0, "No puede ser negativo")
    .max(720, "Máximo 12 horas desde inicio"),
  duration_minutes: z.coerce.number()
    .int()
    .min(1, "Mínimo 1 minuto")
    .max(120, "Máximo 120 minutos"),
  is_mandatory: z.boolean().default(true),
});

export type ShiftBreakFormData = z.infer<typeof shiftBreakSchema>;

// =====================================================
// WORKER SHIFT ASSIGNMENT SCHEMAS
// =====================================================

export const workerShiftAssignmentSchema = z.object({
  worker_id: z.string().uuid("Seleccione un trabajador"),
  shift_id: z.string().uuid("Seleccione un turno"),
  effective_date: z.string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, "Formato de fecha inválido"),
  end_date: z.string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, "Formato de fecha inválido")
    .optional()
    .nullable(),
  notes: z.string().max(500).optional().nullable(),
}).refine((data) => {
  if (data.end_date && data.effective_date > data.end_date) {
    return false;
  }
  return true;
}, {
  message: "La fecha de fin debe ser posterior a la fecha de inicio",
  path: ["end_date"],
});

export type WorkerShiftAssignmentFormData = z.infer<typeof workerShiftAssignmentSchema>;

// =====================================================
// BULK ASSIGNMENT SCHEMA
// =====================================================

export const bulkShiftAssignmentSchema = z.object({
  worker_ids: z.array(z.string().uuid()).min(1, "Seleccione al menos un trabajador"),
  shift_id: z.string().uuid("Seleccione un turno"),
  effective_date: z.string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, "Formato de fecha inválido"),
});

export type BulkShiftAssignmentFormData = z.infer<typeof bulkShiftAssignmentSchema>;
